import React from 'react';
import { CaretDownIcon, NewspaperIcon } from '@phosphor-icons/react';
import { DestaqueTextItem } from './DestaqueCard';
import type { DestaqueCard as DestaqueCardModel } from './helpers';

interface DestaquesMaisMobileProps {
  /** Os destaques secundários (card alto + 2ª linha), na ordem da grade. */
  cards: DestaqueCardModel[];
  isEditMode?: boolean;
}

/**
 * "Mais destaques": os destaques secundários recolhidos, só no mobile.
 *
 * Abaixo de `md` a grade some (ver `DestaquesGrid`) e sobra a manchete. Os
 * outros quatro destaques vêm para cá, sem foto e atrás de um clique — cinco
 * cards com imagem empilhados faziam a faixa custar várias telas de rolagem.
 *
 * `<details>/<summary>` nativo, como o bloco Accordion e o menu lateral: o
 * abre/fecha, o teclado e o anúncio de "expandir/recolher" vêm do navegador,
 * sem hidratação e com o estado certo já no SSR.
 *
 * Não recebe `onEditItem`: o lápis de cada destaque vive no ramo do desktop, e
 * repeti-lo aqui daria dois botões para o mesmo item.
 */
export const DestaquesMaisMobile: React.FC<DestaquesMaisMobileProps> = ({
  cards,
  isEditMode,
}) => {
  if (cards.length === 0) return null;

  return (
    /* Um cartão só, como o "O que você deseja fazer hoje?": a moldura mora
       aqui e as linhas dentro dela ficam sem chrome própria.

       Os dois `divide-y` são independentes e nenhum deles sobra um traço no
       fim: este, sobre os filhos diretos (`<summary>` + `<ul>`), desenha a
       linha entre a barra e o primeiro destaque; o da `<ul>`, logo abaixo,
       separa os destaques entre si.

       `group` para a seta girar com `group-open:` — o `[open]` do `<details>`
       é o próprio estado, não precisa de classe controlada por JS. */
    <details className="mpf-destaques-mais group mt-4 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-surface-container-lowest md:hidden dark:divide-night-850 dark:border-night-850 dark:bg-night-800">
      {/* `list-none` tira o triângulo nativo; o marcador do WebKit antigo sai
          no `_capa-destaques-mais.scss`, que é pseudo-elemento e não cabe em
          utilitário.

          O `rounded-t-lg` não pinta nada (o fundo é do `<details>`): é para o
          anel de foco do teclado acompanhar a curva do cartão em vez de nascer
          com quina reta sobre ela. Sem `overflow-hidden` no `<details>`
          justamente para não recortar esse anel — nenhuma linha tem fundo
          próprio para vazar pelas quinas. */}
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-t-lg p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
        <NewspaperIcon
          aria-hidden="true"
          weight="fill"
          className="h-5 w-5 shrink-0 text-primary dark:text-white"
        />
        {/* Não é heading: os itens da lista são `h3`, como os cards que
            substituem, e um título aqui empurraria todos um nível para baixo
            só no mobile. Cor e corpo, porém, são os dos cabeçalhos de faixa:
            `blue-800` (#1E429F) e não o `primary` (#0B0261), bem mais escuro,
            e 18px como as demais faixas — ao lado de "O que você deseja fazer
            hoje?" as duas diferenças apareciam. Sem variante `md:` porque o
            `<details>` inteiro é `md:hidden`. O chevron acompanha a cor, como
            no card de "Ações rápidas". */}
        <span className="flex-1 text-lg font-semibold text-blue-800 dark:text-white">
          Mais destaques
        </span>
        <CaretDownIcon
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-blue-800 transition-transform group-open:rotate-180 motion-reduce:transition-none dark:text-white"
        />
      </summary>

      <ul className="m-0 flex list-none flex-col divide-y divide-gray-200 p-0 dark:divide-night-850">
        {cards.map((card) => (
          <li key={card.key}>
            <DestaqueTextItem card={card} isEditMode={isEditMode} />
          </li>
        ))}
      </ul>
    </details>
  );
};

export default DestaquesMaisMobile;
